import type { ComponentType } from 'react'

export type Locale = 'et' | 'en' | 'ru' | 'de'

/** UI text in several languages; `en` is the fallback. */
export type LocalizedText = Partial<Record<Locale, string>>

/** A screen contributed by a tenant extension. */
export interface ExtensionView {
    /** Stable id, unique within the extension. */
    id: string
    /** Route path, relative to the app root (e.g. `/smart-actions`). */
    path: string
    title: LocalizedText
    /**
     * Menu group: when the extension's `sections` names this key, the view is listed in that
     * group's dropdown (SDK 1.8); otherwise it's an ordinary menu item.
     */
    section?: string
    /** Roles allowed to see the view; omit or `['*']` for everyone. The API enforces access. */
    roles?: string[]
    /**
     * Where the view is listed: the main navigation (default), the user menu (SDK 1.7), or a
     * button floating in the page's bottom-right corner, on every page but its own (SDK 1.10).
     */
    placement?: 'nav' | 'user-menu' | 'floating'
    /** Who gets the view: everyone (default) or only sales-demo tenants (SDK 1.7). */
    audience?: 'all' | 'demo'
    component: ComponentType
}

/** A view of the main navigation (no placement, or `nav`): not the user menu's, not a floating button's. */
export function inMainMenu(view: Pick<ExtensionView, 'placement'>): boolean {
    return (view.placement ?? 'nav') === 'nav'
}

/** What a tenant extension passes to `registerExtension`. */
export interface ExtensionDefinition {
    /** Tenant slug the extension belongs to. */
    id: string
    version: string
    /** Semver range of the platform SDK the extension was built against, e.g. `^1.0`. */
    sdk: string
    views?: ExtensionView[]
    /**
     * Menu groups by key, each a dropdown titled in the user's language (e.g. `analytics` →
     * "Analüütika"), at the place of its first view (SDK 1.8).
     */
    sections?: Record<string, LocalizedText>
    /**
     * Old addresses that now live elsewhere, e.g. `{ '/reports/smart-actions': '/smart-actions' }`:
     * bookmarks of an earlier app keep working (SDK 1.8). The query string is kept.
     */
    redirects?: Record<string, string>
}

/** Where to load a tenant's extension from, as returned by the tenant boot endpoint. */
export interface ExtensionManifest {
    id: string
    version: string
    sdk: string
    /** Same-origin URL of the extension script. */
    url: string
}
